/**
 * Factory functions for creating container (artifact type) cells
 */
import { dia, shapes, util } from '@joint/core'
import { ArtifactType } from '@/models'
import GraphState from './graph-state'
import { colorFromApiColor } from './color-utils'
import {
  CONTAINER_LABEL_HEIGHT,
  CONTAINER_COVERAGE_BAR_HEIGHT,
  CONTAINER_SEARCH_HEIGHT,
  CONTAINER_ARTIFACT_LIST_TOP,
  ACCENT_BAR_WIDTH,
  ARTIFACT_LEFT_SECTION_WIDTH,
} from './layout-constants'

/**
 * Format artifact count for display (e.g., 32000 -> "32 K")
 */
function formatArtifactCount(count: number): string {
  if (count >= 1000) {
    return `${Math.round(count / 1000)} K`
  }
  return count.toString()
}

function makeCell(opts: dia.Cell.Selectors): shapes.standard.Rectangle {
  const artifact = new shapes.standard.Rectangle()

  artifact.position(100, 30)
  artifact.resize(400, 40)
  artifact.attr('isCell', true)
  artifact.attr(opts)

  return artifact
}

export function makeTypeContainer(artifactType: ArtifactType): shapes.standard.Rectangle {
  const outlineColor = colorFromApiColor(artifactType.color)
  const primaryTextColor = '#212121' // Primary text color for label

  // Use same padding as artifact cells to align with link dot sections
  const horizontalPadding = ARTIFACT_LEFT_SECTION_WIDTH // 40px on each side

  const typeContainer = makeCell({
    body: {
      textAnchor: 'top',
      fill: '#ffffff',
      fillOpacity: 1,
      rx: 5,
      ry: 5,
      stroke: outlineColor,
      strokeWidth: 1,
    },
    accentBar: {
      fill: outlineColor,
      x: 0,
      y: 0,
      width: ACCENT_BAR_WIDTH,
      height: 'calc(h)',
      rx: 5,
      ry: 5,
    },
    label: {
      textAnchor: 'start',
      textVerticalAnchor: 'top',
      x: horizontalPadding + 10, // +10 to align with search field's left margin
      y: 18,
      fill: primaryTextColor,
      fontSize: 16,
      fontWeight: 'bold',
      textWrap: {
        text: artifactType.name?.substring(0, 50),
        width: -(horizontalPadding * 2 + 20), // Account for padding on both sides
        ellipsis: true,
      },
    },
    countBadgeBg: {
      fill: '#ffffff',
      stroke: outlineColor,
      strokeWidth: 1,
      rx: 3,
      ry: 3,
      x: ACCENT_BAR_WIDTH + 4,
      y: -10,
      width: 30,
      height: 20,
    },
    countBadge: {
      textAnchor: 'middle',
      textVerticalAnchor: 'middle',
      x: ACCENT_BAR_WIDTH + 19,
      y: 0,
      fill: '#666666',
      fontSize: 12,
      text: formatArtifactCount(artifactType.artifactCount ?? 0),
    },
    linkCoverageBar: {
      visibility: 'hidden', // Hidden when collapsed
      x: horizontalPadding + 10, // +10 to align with search input's left margin
      y: CONTAINER_LABEL_HEIGHT + 2,
      width: `calc(w - ${horizontalPadding * 2 + 20})`, // Match search input width
      height: 12,
    },
    search: {
      visibility: 'hidden',
      x: horizontalPadding,
      y: CONTAINER_LABEL_HEIGHT + CONTAINER_COVERAGE_BAR_HEIGHT,
      width: `calc(w - ${horizontalPadding * 2})`,
      height: CONTAINER_SEARCH_HEIGHT,
    },
    artifactList: {
      visibility: 'hidden', // Hidden when collapsed
      x: 0,
      y: CONTAINER_ARTIFACT_LIST_TOP,
      width: 'calc(w)',
      height: `calc(h - ${CONTAINER_ARTIFACT_LIST_TOP})`,
    },
  })

  typeContainer.prop(GraphState.PROP_ARTIFACT_TYPE, artifactType)
  typeContainer.resize(420, 60)
  typeContainer.prop('z', 1)

  typeContainer.prop('original_height', typeContainer.size().height)
  typeContainer.prop('original_width', typeContainer.size().width)
  typeContainer.attr('isCell', false)

  typeContainer.markup = util.svg /* xml */ `
      <rect @selector="body" />
      <rect @selector="accentBar" />
      <text @selector="label" />
      <rect @selector="countBadgeBg" />
      <text @selector="countBadge" />
      <foreignObject @selector="search" overflow="hidden">
          <analyze-search-ce @selector="content" artifact-type-id="${artifactType.id}">
          </analyze-search-ce>
      </foreignObject>
      <foreignObject @selector="linkCoverageBar" overflow="hidden">
          <link-coverage-bar-ce artifact-type-id="${artifactType.id}">
          </link-coverage-bar-ce>
      </foreignObject>
      <foreignObject @selector="artifactList" overflow="visible">
          <artifact-list-scroller-ce artifact-type-id="${artifactType.id}">
          </artifact-list-scroller-ce>
      </foreignObject>
  `

  return typeContainer
}
